import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { searchSubscriptions } from "../api/subscriptionSearch";
import { RootState } from "../reducer";
import { SubscriptionSearchResult } from "../types/subscriptionSearch";
import { useAppIdMap } from "./app";
import { usePageBlocks, useSelectedNotebookPageIdMap } from "./page";
import {
  mergeSubscriptionOptions,
  pageSubscriptionOptions,
} from "./subscriptionSearchOptions";
import { useAddSubscriptions } from "./page/block/subscription";
import { getSubscription } from "../api/subscription";
import { isSubscriptionBlock } from "../types/notebook/note/block/subscription";

export const useSubscriptionSearch = (
  config: { disabledSearch?: boolean } = { disabledSearch: false },
) => {
  const { disabledSearch } = config;
  const { appId } = useAppIdMap();
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const { pageBlocks } = usePageBlocks(selectedNotebookPageId);
  const subscriptionMap = useSelector(
    (state: RootState) => state.ui.subscriptionMap ?? {},
  );
  const addSubscriptions = useAddSubscriptions();
  const [searchString, setSearchString] = useState("");
  const [remotePage, setRemotePage] = useState<SubscriptionSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const pageSubscriptionBlocks = (pageBlocks ?? []).filter(isSubscriptionBlock);

    const missingSubIds = pageSubscriptionBlocks
      .map((block) => block["sub-id"])
      .filter((subId) => !subscriptionMap[subId]);

    if (missingSubIds.length === 0) {
      return;
    }

    Promise.all(missingSubIds.map((subId) => getSubscription({ "sub-id": subId })))
      .then((subscriptions) => addSubscriptions(subscriptions))
      .catch(() => undefined);
  }, [addSubscriptions, pageBlocks, subscriptionMap]);

  const handleSearch = useMemo(
    () =>
      debounce((search: string) => {
        if (!appId) {
          setRemotePage([]);
          return;
        }
        setIsLoading(true);
        searchSubscriptions({ "app-id": appId, search })
          .then(setRemotePage)
          .catch(() => {
            setRemotePage([]);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }, 200),
    [appId],
  );

  useEffect(() => {
    if (!disabledSearch) {
      handleSearch(searchString);
    }
  }, [searchString, disabledSearch, handleSearch]);

  const localPage = useMemo(
    () => pageSubscriptionOptions(pageBlocks ?? [], subscriptionMap),
    [pageBlocks, subscriptionMap],
  );
  const page = useMemo(
    () => mergeSubscriptionOptions(localPage, remotePage),
    [localPage, remotePage],
  );

  return { searchString, setSearchString, page, isLoading, setIsLoading };
};
